import axios from "axios";
import { clearToken, getToken, requestPin } from "@/lib/auth";
import { ingestServerDateHeader } from "@/lib/clock-sync";
import * as queue from "@/lib/offline-queue";

/**
 * Thin axios wrapper for the RefClick API.
 *
 * Every backend route lives under `/api` and we always talk to the URL
 * exposed via REACT_APP_BACKEND_URL — never localhost directly, because the
 * Kubernetes ingress is what routes /api → the FastAPI service.
 *
 * Auth model (single-user, PIN-gated writes):
 *   - GET endpoints are fully public — no token attached, no 401 handling
 *     triggers a modal.
 *   - Mutating endpoints (POST/PUT/DELETE/PATCH) get an Authorization
 *     header if a session token is stored in localStorage.
 *   - On a 401 from a mutating call, we clear the token, ask the user
 *     for the PIN (via the bridge into <AuthProvider>), then transparently
 *     replay the original request with the fresh token. If the user
 *     cancels the modal, the original error is re-thrown.
 *
 * Offline model (Phase 2):
 *   - `click(id)` and `undo(id)` check `navigator.onLine` before firing.
 *     When offline, the intent is written to the IndexedDB queue via
 *     `offline-queue.enqueue` and the caller receives an { queued: true }
 *     stub instead of a server response. The Dashboard reads the queue
 *     length to render the "Offline — N pending" chip and drains the
 *     queue when the browser signals `online` again.
 */
// ---------------------------------------------------------------------------
// API base URL resolution — resilient to build-time env misconfiguration.
//
// In preview / local-dev, REACT_APP_BACKEND_URL points at the full backend
// origin (e.g. https://xxx.preview.emergentagent.com). In production the
// frontend and backend are served from the same origin, so a relative
// "/api" path is the correct default even if the env var was NOT injected
// at build time (Sep-2026 incident: a Heroku redeploy shipped a bundle where
// REACT_APP_BACKEND_URL was undefined, and requests went to
// ".../undefined/api/*" and 404'd. Empty-string fallback prevents that
// class of bug entirely — production works whether or not the pipeline
// injects the var, and preview still respects the explicit value).
const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL =
  typeof RAW_BACKEND_URL === "string" && RAW_BACKEND_URL.trim() && RAW_BACKEND_URL !== "undefined"
    ? RAW_BACKEND_URL.trim().replace(/\/+$/, "")
    : "";
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

// One-time visibility hook: if the env var was missing at build time we
// log a single WARN so the same-origin fallback is easy to spot in
// DevTools. Not an error — the app still works — just diagnostics.
if (typeof window !== "undefined" && !BACKEND_URL && !window.__REFCLICK_API_BASE_WARNED__) {
  window.__REFCLICK_API_BASE_WARNED__ = true;
  // eslint-disable-next-line no-console
  console.warn(
    "[RefClick] REACT_APP_BACKEND_URL was not set at build time — falling back to same-origin '/api'. This is fine in production; in local dev, set it in frontend/.env.",
  );
}

const client = axios.create({
  baseURL: API_BASE,
  timeout: 12_000,
});

// Attach the bearer token on writes; leave GETs untouched.
// Also stamp `metadata.startMs` so the response interceptor can compute
// the round-trip time for the clock-sync offset estimator.
client.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (method !== "GET") {
    const t = getToken();
    if (t) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  config.metadata = { ...(config.metadata || {}), startMs: Date.now() };
  return config;
});

// Feed every successful response's Date header into the clock-sync
// estimator. Errors bypass this path since they may not carry a
// trustworthy Date header (network failures, aborts, etc.).
client.interceptors.response.use(
  (r) => {
    try {
      const startMs = r?.config?.metadata?.startMs;
      const dateHeader =
        r?.headers?.date || (r?.headers?.get && r.headers.get("date"));
      if (dateHeader) ingestServerDateHeader(dateHeader, startMs);
    } catch {
      /* clock-sync is best-effort; never break the app on it */
    }
    return r;
  },
);

// On 401 from a mutating call → prompt for PIN, then replay ONCE.
// Auth routes (verify-pin, status) are excluded: their 401s are the user's
// wrong-PIN feedback and MUST reach the caller, not trigger another modal.
client.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;
    const method = (original.method || "get").toUpperCase();
    const url = original.url || "";
    const isAuthRoute = url.startsWith("/auth/") || url.includes("/api/auth/");

    if (
      status === 401 &&
      method !== "GET" &&
      !isAuthRoute &&
      !original._refreshedForAuth
    ) {
      original._refreshedForAuth = true;
      clearToken();
      try {
        await requestPin(); // resolves after PIN succeeds
      } catch {
        throw error; // user cancelled the modal
      }
      const t = getToken();
      if (t) {
        original.headers = { ...(original.headers || {}) };
        original.headers.Authorization = `Bearer ${t}`;
        return client.request(original);
      }
    }
    throw error;
  },
);

export const api = {
  listLinks: () => client.get("/links").then((r) => r.data),
  createLink: (payload) => client.post("/links", payload).then((r) => r.data),
  updateLink: (id, payload) => client.put(`/links/${id}`, payload).then((r) => r.data),
  deleteLink: (id) => client.delete(`/links/${id}`),

  /**
   * Reorder links. Pass the ids in the desired final order; the server
   * rewrites every link's `sort_order` 0..N and returns the enriched
   * list. Today's effective targets stay untouched (invariant guaranteed
   * on the backend — see `/api/links/reorder` docstring).
   */
  reorderLinks: (ids) =>
    client.post("/links/reorder", { ids }).then((r) => r.data),

  // Click / undo are the two endpoints that respect the offline queue.
  click: async (id) => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      await queue.enqueue({ link_id: id, kind: "click" });
      queue.notify();
      return { queued: true };
    }
    return client.post(`/links/${id}/click`).then((r) => r.data);
  },
  undo: async (id) => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      await queue.enqueue({ link_id: id, kind: "undo" });
      queue.notify();
      return { queued: true };
    }
    return client.post(`/links/${id}/undo`).then((r) => r.data);
  },

  // Direct-call variants used by the offline-queue drain — must NOT
  // re-enqueue on failure (that's the queue's own job).
  _clickDirect: (id) => client.post(`/links/${id}/click`).then((r) => r.data),
  _undoDirect: (id) => client.post(`/links/${id}/undo`).then((r) => r.data),

  summary: () => client.get("/summary").then((r) => r.data),

  // Record that the user pressed the ↗ open-in-new-tab button. Server
  // stamps `last_opened_at` and returns the updated link. Purely
  // informational — the frontend renders a 30-minute countdown on the
  // open button from this timestamp. Fire-and-forget from the caller's
  // POV: the tab open happens synchronously via the anchor's default
  // behaviour, this POST just persists the "recently opened" hint.
  markOpened: (id) => client.post(`/links/${id}/opened`).then((r) => r.data),

  // --- Auth --------------------------------------------------------------
  verifyPin: (pin) =>
    client.post("/auth/verify-pin", { pin }).then((r) => r.data),
  authStatus: () => {
    // Send the stored token so the server can verify it; if there is none
    // the endpoint still returns {unlocked: false} without erroring.
    const t = getToken();
    const headers = t ? { Authorization: `Bearer ${t}` } : {};
    return client.get("/auth/status", { headers }).then((r) => r.data);
  },

  // --- IP Fraud Checker --------------------------------------------------
  // Keyless + env-key fan-out. All routes under /api/ipcheck/*.
  ipcheck: {
    providers: () => client.get("/ipcheck/providers").then((r) => r.data),
    // ``browser_tz`` is Intl.DateTimeFormat().resolvedOptions().timeZone;
    // omitting it is still valid (back-compat for curl/API callers).
    check: (ip, note, browser_tz) =>
      client
        .post("/ipcheck/check", { ip, note, browser_tz })
        .then((r) => r.data),
    history: (opts = {}) =>
      client
        .get("/ipcheck/history", { params: opts })
        .then((r) => r.data),
    historyItem: (id) =>
      client.get(`/ipcheck/history/${id}`).then((r) => r.data),
    patchNote: (id, note) =>
      client.patch(`/ipcheck/history/${id}`, { note }).then((r) => r.data),
    recheck: (id) =>
      client.post(`/ipcheck/history/${id}/recheck`).then((r) => r.data),
    del: (id) => client.delete(`/ipcheck/history/${id}`),
    clearAll: () =>
      client.delete("/ipcheck/history", { params: { confirm: "yes" } }),
    legacy: (opts = {}) =>
      client.get("/ipcheck/legacy", { params: opts }).then((r) => r.data),
  },

  // --- Watchlist ---------------------------------------------------------
  // Single-IP persistent watchlist. Auto-populated by /ipcheck/check at
  // combined_score >= 80 (server-side threshold).
  watchlist: {
    list: () => client.get("/watchlist").then((r) => r.data),
    add: (payload) => client.post("/watchlist", payload).then((r) => r.data),
    patch: (id, note) =>
      client.patch(`/watchlist/${id}`, { note }).then((r) => r.data),
    del: (id) => client.delete(`/watchlist/${id}`),
  },

  // --- Maintenance (backup / restore / clear month) ---------------------
  maintenance: {
    // Backup must be streamed as a Blob so the browser can offer the
    // "Save as" dialog without the response ever being touched as JSON.
    backup: () =>
      client.get("/maintenance/backup", { responseType: "blob" }).then((r) => ({
        blob: r.data,
        filename:
          /filename="([^"]+)"/.exec(r.headers?.["content-disposition"] || "")?.[1] ||
          "refclick-backup.json",
      })),
    restore: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return client
        .post("/maintenance/restore", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
    },
  },

  // --- Telegram weekly reports -----------------------------------
  // Credentials + enabled flag stored in the settings doc so they
  // survive redeploys. Config endpoint returns a masked token for
  // display; a PATCH with an empty bot_token leaves the saved value
  // alone (we never send it from the frontend unless the user types
  // a new one).
  telegramReports: {
    getConfig: () =>
      client.get("/telegram/report/config").then((r) => r.data),
    patchConfig: (patch) =>
      client.patch("/telegram/report/config", patch).then((r) => r.data),
    sendNow: () =>
      client.post("/telegram/send-report").then((r) => r.data),
  },
};
